import type { PriceLineResponse } from '../model/types';

export interface GridCellResolution {
  line: PriceLineResponse;
  inherited: boolean;
}

/**
 * Returns true when the line's state is ACTIVE and the current time falls
 * within the half-open validity window [validFrom, validTo).
 * Missing bounds are treated as open (no lower/upper limit).
 */
function isWindowActive(line: PriceLineResponse, now: Date): boolean {
  if (line.state !== 'ACTIVE') return false;
  if (line.validFrom && now < new Date(line.validFrom)) return false;
  if (line.validTo && now >= new Date(line.validTo)) return false;
  return true;
}

/**
 * Among lines with state === 'ACTIVE' whose validity window covers `now` (half-open [validFrom, validTo)),
 * find the line for `channelCode` (null = base line where channelCode is null/undefined).
 * If channelCode is non-null and has no own line, fall back to the base line with inherited: true.
 * Returns null if neither exists.
 */
export function resolveGridCell(
  lines: PriceLineResponse[],
  channelCode: string | null,
  now: Date,
): GridCellResolution | null {
  const active = lines.filter((l) => isWindowActive(l, now));
  const baseLine = active.find((l) => !l.channelCode);

  if (channelCode === null) {
    return baseLine ? { line: baseLine, inherited: false } : null;
  }

  const ownLine = active.find((l) => l.channelCode === channelCode);
  if (ownLine) return { line: ownLine, inherited: false };

  return baseLine ? { line: baseLine, inherited: true } : null;
}

/** Returns the amount of the tier whose minQuantity exactly matches `minQuantity`, or undefined. */
export function tierAmount(line: PriceLineResponse, minQuantity: number): number | undefined {
  return line.tiers.find((t) => t.minQuantity === minQuantity)?.amount;
}

/** The unit amount in effect at `quantity`: the amount of the greatest tier with minQuantity <= quantity. */
export function effectiveTierAmount(line: PriceLineResponse, quantity: number): number | undefined {
  const applicable = (line.tiers ?? [])
    .filter((t) => t.minQuantity <= quantity)
    .sort((a, b) => b.minQuantity - a.minQuantity);
  return applicable[0]?.amount;
}
